import type {
  ValidationErrorIssue,
  ValidationErrorMessage,
  ValidationOverride,
} from '@festina/life-general-types/life'
import { notEmpty } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Alert } from '@festina-life/ui/feedback'
import { InputWrapper, RawTextArea } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { CardBody, Stack } from '@festina-life/ui/structure'
import { type ComponentProps, useState } from 'react'

type Severity = ComponentProps<typeof Alert>['severity']

const toSeverity = (
  issueSeverity: ValidationErrorIssue['severity'],
  isOverridden: boolean,
): Severity => {
  const severityMap: Record<string, Severity> = {
    error: 'error',
    warning: isOverridden ? 'info' : 'warning',
    info: 'info',
    errorOverridable: isOverridden ? 'info' : 'warning',
  }

  return severityMap[issueSeverity ?? 'error'] ?? 'error'
}

/** Returns an issue itself and all of its descendants as a flat array. */
const flattenIssues = (issue: ValidationErrorIssue): ValidationErrorIssue[] => [
  issue,
  ...(issue.subIssues?.filter(notEmpty).flatMap(flattenIssues) ?? []),
]

type Props = {
  error: ValidationErrorMessage
  validationOverrides: ValidationOverride[]
  onAddValidationOverride: (newValue: ValidationOverride) => void
}

export const ValidationErrorMessageAlert = ({
  error,
  validationOverrides,
  onAddValidationOverride,
}: Props) => {
  const translate = useTranslate()
  const [showOverrideInput, setShowOverrideInput] = useState(false)
  const [overrideMessage, setOverrideMessage] = useState('')

  return (
    <Stack className="flex-col">
      {error.issues?.filter(notEmpty)?.map((issue) => {
        const flatIssues = flattenIssues(issue)
        const flatSubIssues = flatIssues.slice(1) // Exclude the parent issue.
        const isOverridden = flatIssues.every(
          (subIssue) =>
            subIssue.severity !== 'errorOverridable' ||
            validationOverrides.some(
              (override) => override.validationOverrideKey === subIssue.key,
            ),
        )

        const handleSaveOverride = () => {
          for (const issue of flatIssues) {
            if (issue.severity === 'errorOverridable') {
              onAddValidationOverride({
                _type: 'ValidationOverride',
                validationOverrideKey: issue.key,
                reason: overrideMessage,
              })
            }
          }

          setShowOverrideInput(false)
          setOverrideMessage('')
        }

        const isErrorOverridable = flatIssues.some(
          (subIssue) => subIssue.severity === 'errorOverridable',
        )
        const isOverridable =
          isErrorOverridable && !showOverrideInput && !isOverridden

        return (
          <Alert
            key={issue.key}
            title={issue.key ?? ''}
            severity={toSeverity(
              isErrorOverridable ? 'errorOverridable' : issue.severity, // ValidationErrorIssue with the error or some of its subIssues having severity of errorOverridable are considered to always have severity errorOverridable in toSeverity function.
              isOverridden,
            )}
            action={
              isOverridable ? (
                <Button
                  variant="discreet"
                  onClick={() => setShowOverrideInput(true)}
                  className="text-inherit!"
                >
                  {translate('validationMessage.overrideButtonText')}
                </Button>
              ) : null
            }
          >
            {showOverrideInput && (
              <CardBody outerClassName="my-1.5">
                <InputWrapper
                  label={translate('validationMessage.overrideMessage')}
                >
                  <RawTextArea
                    value={overrideMessage}
                    autoFocus
                    rows={1}
                    onChange={(value) =>
                      setOverrideMessage(value.currentTarget.value)
                    }
                  />
                </InputWrapper>
                <Button className="self-end" onClick={handleSaveOverride}>
                  {translate('add')}
                </Button>
              </CardBody>
            )}
            {issue.message}
            {flatSubIssues.length > 0 && (
              <ul className="list-disc">
                {flatSubIssues.map((subIssue) => (
                  <li className="ms-4" key={subIssue.key}>
                    {subIssue.message}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )
      })}
    </Stack>
  )
}
